import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { BarChart3, TrendingUp, TrendingDown, Minus, ShoppingCart, Euro, Percent } from 'lucide-react';
import { motion } from 'framer-motion';

const PERIODS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: '7 jours' },
  { value: 'month', label: 'Mois' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Année' },
];

function Change({ current, previous, suffix = '' }) {
  if (!previous) return null;
  const diff = current - previous;
  const pct = previous > 0 ? ((diff / previous) * 100).toFixed(1) : 0;
  if (diff > 0) return <span className="text-[#00FF88] text-xs flex items-center gap-0.5"><TrendingUp className="h-3 w-3" />+{pct}%{suffix}</span>;
  if (diff < 0) return <span className="text-[#FF3B5C] text-xs flex items-center gap-0.5"><TrendingDown className="h-3 w-3" />{pct}%{suffix}</span>;
  return <span className="text-[#6B6B6E] text-xs flex items-center gap-0.5"><Minus className="h-3 w-3" />0%</span>;
}

export default function InfluencerStats() {
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/influencer/stats?period=${period}&compare=true`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  const c = stats?.current || {};
  const p = stats?.previous || {};

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="influencer-stats-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-[#FF6B35]" />Statistiques
          </h1>
          <p className="text-[#6B6B6E] text-sm mt-1">Suivez vos performances</p>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="period-filters">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                period === p.value ? 'bg-[#FF6B35] text-black' : 'bg-[#222226] text-[#B8B8BA] hover:bg-[#3A3A3D]'
              }`} data-testid={`filter-${p.value}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-[#2A2A2D] rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid="stat-sales">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#6B6B6E] text-xs font-semibold uppercase">Ventes</span>
                <ShoppingCart className="h-4 w-4 text-[#FF6B35] opacity-60" />
              </div>
              <div className="text-2xl font-bold text-white">{c.nb_sales || 0}</div>
              <Change current={c.nb_sales} previous={p.nb_sales} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid="stat-revenue">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#6B6B6E] text-xs font-semibold uppercase">CA généré</span>
                <Euro className="h-4 w-4 text-[#00FFFF] opacity-60" />
              </div>
              <div className="text-2xl font-bold text-white">{(c.total_amount || 0).toFixed(2)}€</div>
              <Change current={c.total_amount} previous={p.total_amount} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid="stat-commission">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#6B6B6E] text-xs font-semibold uppercase">Commission</span>
                <TrendingUp className="h-4 w-4 text-[#00FF88] opacity-60" />
              </div>
              <div className="text-2xl font-bold text-[#00FF88]">{(c.total_commission || 0).toFixed(2)}€</div>
              <Change current={c.total_commission} previous={p.total_commission} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid="stat-conversion">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#6B6B6E] text-xs font-semibold uppercase">Conversion</span>
                <Percent className="h-4 w-4 text-[#FFB800] opacity-60" />
              </div>
              <div className="text-2xl font-bold text-white">{c.conversion_rate || 0}%</div>
              <Change current={c.conversion_rate} previous={p.conversion_rate} />
            </motion.div>
          </div>

          {/* Comparison Info */}
          {stats?.previous && (
            <div className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40">
              <p className="text-[#6B6B6E] text-xs mb-3 uppercase font-semibold">Période précédente</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div><span className="text-[#6B6B6E]">Ventes : </span><span className="text-white font-medium">{p.nb_sales || 0}</span></div>
                <div><span className="text-[#6B6B6E]">CA : </span><span className="text-white font-medium">{(p.total_amount || 0).toFixed(2)}€</span></div>
                <div><span className="text-[#6B6B6E]">Commission : </span><span className="text-white font-medium">{(p.total_commission || 0).toFixed(2)}€</span></div>
                <div><span className="text-[#6B6B6E]">Conversion : </span><span className="text-white font-medium">{p.conversion_rate || 0}%</span></div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
